import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Spinner,
  Text,
  VStack
} from '@chakra-ui/react';

import { authService } from '../services/authService';
import { DealerSummary, masterService } from '../services/masterService';

export const SelectDealerPage: React.FC = () => {
  const [dealers, setDealers] = useState<DealerSummary[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const loadDealers = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await masterService.listDealers();
        setDealers(data);
      } catch (err: any) {
        setError(err.message || 'Não foi possível carregar as concessionárias.');
      } finally {
        setIsLoading(false);
      }
    };

    loadDealers();
  }, []);

  const filteredDealers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return dealers;
    return dealers.filter(dealer =>
      dealer.name.toLowerCase().includes(term) ||
      dealer.cnpj.includes(term) ||
      dealer.dealerNetworkName.toLowerCase().includes(term)
    );
  }, [dealers, search]);

  const handleSelectDealer = async (dealer: DealerSummary) => {
    try {
      setImpersonatingId(dealer.id);
      setError(null);

      const response = await masterService.impersonate(dealer.id);
      authService.setImpersonationSession(response.token, {
        id: response.dealerId,
        name: response.dealerName,
        cnpj: response.dealerCnpj
      });

      navigate('/home', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Não foi possível acessar essa concessionária.');
    } finally {
      setImpersonatingId(null);
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <Flex
        bg="linear-gradient(135deg, #3333CC 0%, #2828A0 100%)"
        py={4}
        px={6}
        alignItems="center"
        justifyContent="space-between"
      >
        <Heading size="md" color="white">
          Selecionar Concessionária
        </Heading>
        <Button
          size="sm"
          bg="rgba(255,255,255,0.15)"
          color="white"
          borderWidth="1px"
          borderColor="rgba(255,255,255,0.3)"
          _hover={{ bg: 'rgba(255,255,255,0.25)' }}
          onClick={handleLogout}
        >
          Sair
        </Button>
      </Flex>

      <Box maxW="800px" mx="auto" py={8} px={4}>
        <Text color="gray.600" mb={4}>
          Escolha a concessionária em nome de quem você deseja emitir uma apólice.
        </Text>

        <Input
          placeholder="Buscar por nome, CNPJ ou rede..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="lg"
          bg="white"
          borderRadius="md"
          mb={6}
          _focus={{ borderColor: '#3333CC', boxShadow: '0 0 0 1px #3333CC' }}
        />

        {error && (
          <Box bg="red.50" p={3} borderRadius="md" borderLeft="4px solid" borderLeftColor="red.500" mb={4}>
            <Text color="red.600" fontSize="sm">{error}</Text>
          </Box>
        )}

        {isLoading ? (
          <Flex justify="center" py={12}>
            <Spinner size="lg" color="#3333CC" />
          </Flex>
        ) : filteredDealers.length === 0 ? (
          <Text color="gray.500" textAlign="center" py={12}>
            Nenhuma concessionária encontrada.
          </Text>
        ) : (
          <VStack gap={3} align="stretch">
            {filteredDealers.map((dealer) => (
              <Flex
                key={dealer.id}
                bg="white"
                borderRadius="md"
                boxShadow="0 1px 3px rgba(0,0,0,0.1)"
                p={4}
                alignItems="center"
                justifyContent="space-between"
                borderLeft="4px solid"
                borderLeftColor="#3333CC"
              >
                <Box>
                  <Text fontWeight="bold" color="#1A1A1A">{dealer.name}</Text>
                  <Text fontSize="sm" color="gray.600">
                    CNPJ: {dealer.cnpj} · Rede: {dealer.dealerNetworkName}
                  </Text>
                </Box>
                <Button
                  bg="#3333CC"
                  color="white"
                  size="sm"
                  _hover={{ bg: '#2828A0' }}
                  disabled={impersonatingId !== null}
                  onClick={() => handleSelectDealer(dealer)}
                >
                  {impersonatingId === dealer.id ? 'Acessando...' : 'Emitir por esta concessionária'}
                </Button>
              </Flex>
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  );
};
